import { useEffect, useRef } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSegments } from 'expo-router';
import { useUserStore } from '@/state/userStore';
import { STORAGE_ONBOARDING_COMPLETED_AT_MS } from '@/constants/onboarding';
import { useOfflinePlanTierDisplay } from '@/hooks/useOfflinePlanTierDisplay';
import { usePremiumUpsellStore } from '@/state/premiumUpsellStore';

const STORAGE_KEY = '@tripvio_marketing_upsell_last_shown_at';
/** Minimum time between auto-showing the marketing upsell (ms). */
const COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000;
/** Earliest auto-show after first recorded onboarding completion on this install. */
const MIN_MS_AFTER_ONBOARDING = 5 * 60 * 1000;

/**
 * Shows the full-screen Tripvio Pro marketing upsell occasionally for signed-in,
 * onboarded, non-premium users while they are in the main tab shell.
 *
 * Deferred while the post-onboarding spotlight tutorial may still appear — both use
 * fullscreen modals and stacking blocked the upsell dismiss control.
 *
 * After onboarding, waits until at least five minutes have passed since that completion
 * (and still respects COOLDOWN_MS). No stored onboarding time (e.g. legacy session) → no
 * extra wait beyond eligibility + cooldown.
 */
export function PremiumUpsellAutoTrigger() {
  const authReady = useUserStore((s) => s.authReady);
  const firebaseUser = useUserStore((s) => s.firebaseUser);
  const hasCompletedOnboarding = useUserStore((s) => s.hasCompletedOnboarding);
  const shouldShowTutorial = useUserStore((s) => s.shouldShowTutorial);
  const hasCompletedTutorial = useUserStore((s) => s.hasCompletedTutorial);
  const profile = useUserStore((s) => s.profile);
  const segments = useSegments();
  const open = usePremiumUpsellStore((s) => s.open);
  /** One auto-show attempt per signed-in user session (avoids re-scheduling on tab switches). */
  const sessionAttemptedRef = useRef(false);
  const offlinePlan = useOfflinePlanTierDisplay(firebaseUser?.uid, {
    isPremium: profile?.isPremium,
    planTier: profile?.planTier,
    entitlement: profile?.entitlement,
  });
  const isPremium = offlinePlan.effectivePremium;
  const effectivePremiumRef = useRef(isPremium);
  effectivePremiumRef.current = isPremium;

  useEffect(() => {
    sessionAttemptedRef.current = false;
  }, [firebaseUser?.uid]);

  useEffect(() => {
    if (!authReady || !firebaseUser?.uid || !hasCompletedOnboarding || isPremium) {
      return;
    }
    if (shouldShowTutorial && !hasCompletedTutorial) {
      return;
    }
    if (segments[0] !== '(tabs)') {
      return;
    }
    if (sessionAttemptedRef.current) {
      return;
    }
    sessionAttemptedRef.current = true;

    let cancelled = false;
    let openTimerId: ReturnType<typeof setTimeout> | null = null;

    void (async () => {
      let delayMs = 0;
      try {
        const rawAt = await AsyncStorage.getItem(STORAGE_ONBOARDING_COMPLETED_AT_MS);
        const at = rawAt ? parseInt(rawAt, 10) : NaN;
        if (!Number.isNaN(at)) {
          delayMs = Math.max(0, at + MIN_MS_AFTER_ONBOARDING - Date.now());
        }
      } catch {
        // ignore
      }

      if (cancelled) return;

      openTimerId = setTimeout(() => {
        void (async () => {
          try {
            const raw = await AsyncStorage.getItem(STORAGE_KEY);
            const now = Date.now();
            if (raw) {
              const last = parseInt(raw, 10);
              if (!Number.isNaN(last) && now - last < COOLDOWN_MS) {
                return;
              }
            }
            if (cancelled) return;
            if (effectivePremiumRef.current) {
              return;
            }
            open({ source: 'app_launch_marketing' });
            await AsyncStorage.setItem(STORAGE_KEY, String(now));
          } catch {
            // ignore storage failures
          }
        })();
      }, delayMs);
    })();

    return () => {
      cancelled = true;
      if (openTimerId != null) {
        clearTimeout(openTimerId);
      }
    };
  }, [
    authReady,
    firebaseUser?.uid,
    hasCompletedOnboarding,
    hasCompletedTutorial,
    isPremium,
    open,
    segments,
    shouldShowTutorial,
  ]);

  return null;
}
